import { Link } from "react-router-dom";
import styled from "styled-components";

export const Container = styled.div`
  background: var(--bg-page);
  min-height: 100vh;
  padding-bottom: var(--space-10);
`;

export const Content = styled.div`
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  padding: var(--space-6) var(--space-5);

  @media (max-width: 560px) {
    padding: var(--space-4) var(--space-3);
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-6);

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }
`;

export const PageLocationTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--navy);
  margin: 0;

  @media (max-width: 560px) {
    font-size: 1.2rem;
  }
`;

export const SectionTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gray-400);
  margin: var(--space-6) 0 var(--space-3) 0;
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--border);

  @media (max-width: 560px) {
    margin: var(--space-4) 0 var(--space-2) 0;
    font-size: 0.72rem;
  }
`;

export const PageTitle = styled.span`
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--gray-400);
`;

export const AthletesLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--navy);
  color: #fff;
  border-radius: var(--radius-sm);
  padding: 10px 18px;
  font-family: var(--font-display);
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-decoration: none;
  transition: var(--transition);
  white-space: nowrap;

  &:hover {
    background: var(--navy-light);
    transform: translateY(-1px);
  }

  @media (max-width: 560px) {
    padding: 8px 14px;
    font-size: 0.72rem;
    width: 100%;
    justify-content: center;
  }
`;

/* ── Card ── */

export const AthleteCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  display: flex;
  align-items: center;
  gap: var(--space-4);
  transition: var(--transition);
  margin-bottom: var(--space-3);

  &:hover {
    border-color: var(--border-hover);
    box-shadow: var(--shadow-sm);
  }

  @media (max-width: 560px) {
    padding: var(--space-3);
    gap: var(--space-3);
  }
`;

export const AthleteAvatar = styled.div<{ $src?: string }>`
  width: 52px;
  height: 52px;
  border-radius: var(--radius-full);
  background: ${({ $src }) => $src ? `url(${$src}) center/cover` : "var(--gray-100)"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--gray-200);
  overflow: hidden;
  font-size: 1.2rem;
  color: var(--gray-400);
  font-weight: 700;

  @media (max-width: 560px) {
    width: 44px;
    height: 44px;
    font-size: 1rem;
  }
`;

export const AthleteInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const AthleteNameRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

export const AthleteName = styled.span`
  font-family: var(--font-display);
  font-size: 0.92rem;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--navy);
  letter-spacing: 0.01em;

  @media (max-width: 560px) {
    font-size: 0.82rem;
  }
`;

export const AthleteAge = styled.span`
  font-family: var(--font-body);
  font-size: 0.7rem;
  color: var(--gray-400);
  font-weight: 500;
`;

export const AthleteMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
`;

export const BeltBadge = styled.span<{ $belt?: number }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: var(--radius-full);
  font-family: var(--font-display);
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ $belt }) => {
    if (!$belt || $belt <= 1) return "var(--gray-700)";
    if ($belt >= 6) return "#fff";
    return "var(--gray-700)";
  }};
  background: ${({ $belt }) => {
    if (!$belt || $belt <= 1) return "#f0f0f0";
    if ($belt === 2) return "#d1d5db";
    if ($belt === 3) return "#fde68a";
    if ($belt === 4) return "#fdba74";
    if ($belt === 5) return "#86efac";
    if ($belt === 6) return "#3b82f6";
    if ($belt === 7) return "#8b5cf6";
    if ($belt === 8) return "#92400e";
    if ($belt === 9) return "#1a1a1a";
    return "#f0f0f0";
  }};
`;

export const MainAthleteTag = styled.span`
  display: inline-flex;
  padding: 2px 6px;
  border-radius: var(--radius-full);
  font-family: var(--font-body);
  font-size: 0.55rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--success-500);
  background: rgba(14, 165, 114, 0.1);
`;

/* ── Actions (olhinho + editar) ── */

export const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
`;

export const EyeButton = styled.button`
  background: var(--gray-100);
  border: 1.5px solid var(--gray-200);
  color: var(--navy);
  cursor: pointer;
  padding: 8px;
  font-size: 1rem;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  width: 34px;
  height: 34px;

  &:hover {
    background: var(--navy);
    color: #fff;
    border-color: var(--navy);
    transform: scale(1.08);
  }
`;

export const EditButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--navy);
  color: #fff;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  text-decoration: none;
  transition: var(--transition);

  &:hover {
    background: var(--navy-light);
    transform: scale(1.05);
  }
`;

/* ── Detalhes expandidos ── */

export const ExpandedDetails = styled.div`
  margin-top: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const AthleteDetail = styled.span<{ $hideOnMobile?: boolean }>`
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: var(--gray-500);
  line-height: 1.6;
`;

export const AthleteDetailLabel = styled.span`
  font-weight: 600;
  color: var(--gray-600);
`;

/* ── Unused exports (kept for compatibility) ── */
export const PageTitleContainer = styled.div``;
export const SubscriptionContainer = styled.div``;
export const SubscriptionContent = styled.div``;
export const SubscriptionDescription = styled.div``;
export const SubscriptionItem = styled.div``;
export const SubscriptionList = styled.a``;
